import Papa from 'papaparse';
import ExcelJS from 'exceljs';

export interface IParsedTable {
  /** Column headers from the first row. */
  headers: string[];
  /** Data rows; cells are always strings. */
  rows: string[][];
}

function parseCsv(blob: Blob): Promise<IParsedTable> {
  return new Promise((resolve, reject) => {
    Papa.parse<string[]>(blob as File, {
      skipEmptyLines: 'greedy',
      complete: results => {
        // Papa flags UndetectableDelimiter on single-column files, then parses
        // them fine with ','. Only bail on errors that broke the parse.
        const fatal = results.errors.filter(e => e.type !== 'Delimiter');
        if (fatal.length > 0) {
          reject(new Error(fatal[0].message));
          return;
        }
        const data = results.data;
        if (data.length === 0) {
          resolve({ headers: [], rows: [] });
          return;
        }
        resolve({ headers: data[0], rows: data.slice(1) });
      },
      error: err => reject(err),
    });
  });
}

function readBlobAsText(blob: Blob): Promise<string> {
  // FileReader works on every target we ship to (incl. jsdom in tests);
  // `Blob.text()` and `Response.text()` are spottier in older Safari/jsdom.
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result ?? ''));
    reader.onerror = () => reject(reader.error);
    reader.readAsText(blob);
  });
}

async function parseJson(blob: Blob): Promise<IParsedTable> {
  const text = await readBlobAsText(blob);
  let data: unknown;
  try {
    data = JSON.parse(text);
  } catch (err) {
    throw new Error(
      `Invalid JSON: ${err instanceof Error ? err.message : String(err)}`,
    );
  }
  if (!Array.isArray(data)) {
    throw new Error('JSON root must be an array of objects');
  }
  if (data.length === 0) {
    return { headers: [], rows: [] };
  }
  // Union of keys preserves columns from sparse objects.
  const headers = Array.from(
    new Set(
      data.flatMap(obj =>
        obj && typeof obj === 'object' ? Object.keys(obj) : [],
      ),
    ),
  );
  const rows = data.map(obj => {
    const record = (obj ?? {}) as Record<string, unknown>;
    return headers.map(h => {
      const v = record[h];
      if (v == null) return '';
      return typeof v === 'object' ? JSON.stringify(v) : String(v);
    });
  });
  return { headers, rows };
}

async function parseXlsx(blob: Blob): Promise<IParsedTable> {
  const arrayBuffer = await blob.arrayBuffer();
  const workbook = new ExcelJS.Workbook();
  await workbook.xlsx.load(arrayBuffer);
  const worksheet = workbook.worksheets[0];
  if (!worksheet) throw new Error('Spreadsheet has no sheets');

  const rows: string[][] = [];
  worksheet.eachRow(row => {
    // `cell.text` resolves formula/hyperlink/rich-text/error shapes to strings.
    const denseRow = Array.from(
      { length: row.cellCount },
      (_, i) => row.getCell(i + 1).text ?? '',
    );
    rows.push(denseRow);
  });
  return { headers: rows[0] ?? [], rows: rows.slice(1) };
}

// Legacy binary Excel formats (BIFF .xls, binary .xlsb). ExcelJS only reads
// OOXML (.xlsx), so these must be rejected before load() to avoid a cryptic
// parse error. Callers use this to surface a clear "re-save as .xlsx" message.
export function isLegacyExcelFileName(name: string): boolean {
  const ext = (name.split('.').pop() ?? '').toLowerCase();
  return ext === 'xls' || ext === 'xlsb';
}

export async function parseTabularFile(file: File): Promise<IParsedTable> {
  const ext = (file.name.split('.').pop() ?? '').toLowerCase();
  if (ext === 'csv') return parseCsv(file);
  if (ext === 'json') return parseJson(file);
  if (isLegacyExcelFileName(file.name)) {
    throw new Error(
      `Legacy Excel files (.${ext}) aren't supported. Re-save as .xlsx and try again.`,
    );
  }
  if (ext === 'xlsx') return parseXlsx(file);
  throw new Error(`Unsupported file type: .${ext}`);
}
